/*
 * Reading the server's per-action results for the offline action buffer. An action is identified by its
 * actionType + loggedAt, with loggedAt always compared as epoch milliseconds. A buffered action may be
 * deleted only when a result names it, says succeeded, and carries a status known to mean durably handled;
 * anything else keeps the action for a retry. See "Buffered actions are deleted only by their own acknowledgement" in CLAUDE.md.
 */
import { IUserActionAck } from '../types';
import { STATUS, SUCCEEDED_STATUSES } from '../status.constants';
import { toEpochMillis } from './date.helpers';

const SUCCEEDED_ACK_STATUSES: ReadonlySet<string> = new Set<string>(SUCCEEDED_STATUSES);

// The one identity for a buffered action and its result, whatever format loggedAt arrives in (Date, ISO
// string or epoch ms). Undefined when either part is missing or invalid, so it never matches anything.
export const actionKey = (action: unknown): string | undefined => {
  if (!action || typeof action !== 'object') return undefined;
  const { actionType, loggedAt } = action as { actionType?: unknown; loggedAt?: unknown };
  if (typeof actionType !== 'string' || !actionType) return undefined;
  if (!(typeof loggedAt === 'string' || typeof loggedAt === 'number' || loggedAt instanceof Date)) return undefined;
  const millis = toEpochMillis(loggedAt);
  return Number.isFinite(millis) ? `${actionType}|${millis}` : undefined;
};

export const isSucceededAck = (ack: unknown): ack is IUserActionAck => {
  if (!actionKey(ack)) return false;
  const { succeeded, status } = ack as IUserActionAck;
  return succeeded === true && typeof status === 'string' && SUCCEEDED_ACK_STATUSES.has(status);
};

// Saved and in effect: a refusal is saved (recorded, so the device may delete it) but was never applied.
export const isAppliedAck = (ack: unknown): boolean => isSucceededAck(ack) && ack.status !== STATUS.REFUSED;

export const findAck = (results: unknown, action: IUserAction): IUserActionAck | undefined => {
  const key = actionKey(action);
  if (!key || !Array.isArray(results)) return undefined;
  return results.find((result) => actionKey(result) === key);
};

// Two results sharing one key cannot be told apart — the key is actionType + loggedAt, with no
// target — so acking on "any of them matched" would delete a score that failed alongside one that
// applied. Neither is acked: both stay buffered and the retry sorts them out.
export const ambiguousAckKeys = (results: unknown): Set<string> => {
  const seen = new Map<string, number>();
  if (!Array.isArray(results)) return new Set();
  results.forEach((result) => {
    const key = actionKey(result);
    if (key) seen.set(key, (seen.get(key) || 0) + 1);
  });
  return new Set(Array.from(seen.entries()).filter(([, count]) => count > 1).map(([key]) => key));
};

const sentActionKeysMatching = (sent: IUserAction[], results: unknown, matches: (ack: unknown) => boolean): Set<string> => {
  const matched = new Set<string>();
  if (!Array.isArray(results)) return matched;
  const sentKeys = new Set(sent.map(actionKey).filter((key): key is string => !!key));
  const ambiguous = ambiguousAckKeys(results);
  if (ambiguous.size) {
    console.warn('Ambiguous acknowledgements; keeping those actions for the next sync', { keys: Array.from(ambiguous) });
  }
  results.forEach((result) => {
    const key = actionKey(result);
    if (key && !ambiguous.has(key) && sentKeys.has(key) && matches(result)) matched.add(key);
  });
  return matched;
};

const isRefusedAck = (ack: unknown): boolean => isSucceededAck(ack) && !isAppliedAck(ack);

export const ackedActionKeys = (sent: IUserAction[], results: unknown): Set<string> =>
  sentActionKeysMatching(sent, results, isSucceededAck);

// Score actions in ackedActionKeys that were refused: deleted like any saved action, never applied.
// Player edits share the buffer but report their own refusals, so they aren't counted here.
export const refusedScoreActionKeys = (sent: IUserAction[], results: unknown): Set<string> =>
  sentActionKeysMatching(sent.filter((action) => action.actionType !== 'PLAYER_EDIT'), results, isRefusedAck);

// The server's reason for each refusal, deduped. Without it the scorer gets a bare count and has
// nothing to act on. A server that sends none yields [], and the caller keeps the generic line.
export const refusedScoreReasons = (sent: IUserAction[], results: unknown): string[] => {
  if (!Array.isArray(results)) return [];
  const refusedKeys = refusedScoreActionKeys(sent, results);
  const reasons = results
    .filter((result) => refusedKeys.has(actionKey(result) as string))
    .map((result) => (result as IUserActionAck).refusalReason)
    .filter((reason): reason is string => typeof reason === 'string' && reason.trim().length > 0);
  return Array.from(new Set(reasons));
};
